import { useEffect, useMemo } from "react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Button } from "@mui/material";
import { tripQueryOptions } from "@/app/lib/hooks/useTrips";
import { PersistentDrawer } from "@/app/ui/PersistentDrawer";
import type { BookingData } from "@/app/ui/trips/types";
import { OverviewCard } from "@/app/ui/trips/bookingFlow/summary/OverviewCard";
import { TripDetailsCard } from "@/app/ui/trips/bookingFlow/summary/TripDetailsCard";
import {
  TravellerListCard,
  TravellerListItem,
} from "@/app/ui/trips/TravellerListCard";
import { PriceDetailsCard } from "@/app/ui/trips/bookingFlow/summary/PriceDetailsCard";
import { TermsAndConditionsCard } from "@/app/ui/trips/bookingFlow/summary/TermsAndConditionsCard";
import { usePurchaseStateContext } from "@/app/lib/context/PurchaseStateContext";
import { useTripBooking } from "@/app/lib/hooks/useTripBooking";
import { PurchaseStatus } from "@/app/lib/payments/types";
import { rudderAnalytics, TripTrackingEvents } from "@/app/lib/rudderstack";
import { useTripPrice } from "@/app/ui/trips/useTripPrice";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";
import { allTravellerAgeGroups, type Traveller } from "@/app/lib/types/types";
import { BannersPlacementSlug } from "@/app/lib/types/enums";
import { BannersPlacement } from "@/app/ui/banners-placement/BannersPlacement";

export const Route = createFileRoute("/_authenticated/trips/$tripId/summary")({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const { tripId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: trip } = useSuspenseQuery(tripQueryOptions(tripId));
  const { updatePurchaseState } = usePurchaseStateContext();
  const { getFieldState, formState, handleSubmit } =
    useFormContext<BookingData>();
  const { invalid: termsAndConditionsInvalid } = getFieldState(
    "termsAndConditions",
    formState,
  );
  const { totalPrice, totalDiscount, totalDiscountedPrice } =
    useTripPrice(trip);
  const { data: travellers } = useQuery(travellersQueryOptions());
  const travellerSlots = useWatch<BookingData, "selectedTravellers">({
    name: "selectedTravellers",
  });

  const travellersArray = useMemo(() => {
    const travellersById: Record<string, Traveller> = {};
    for (const traveller of travellers || []) {
      travellersById[traveller.id] = traveller;
    }

    return allTravellerAgeGroups
      .map((ageGroup) =>
        travellerSlots[ageGroup]
          .filter((x): x is NonNullable<typeof x> => Boolean(x))
          .map((travellerId, index) => {
            const traveller = travellersById[travellerId];

            return {
              id: traveller.id,
              name: `${traveller.firstName} ${traveller.lastName}`,
              additionalData: [
                t("travellers.travellerTypeN", {
                  type: ageGroup,
                  number: index + 1,
                }),
                `${traveller.passportNumber.slice(0, 3)}****
                      ${traveller.passportNumber.slice(-2)}`,
              ],
            };
          }),
      )
      .flat();
  }, [travellers, travellerSlots, t]);

  const {
    qiPayment: { isPending: isQiPaymentPending, mutateAsync },
  } = useTripBooking({
    onPaymentSuccess: ({ orderId }) => {
      rudderAnalytics.track(TripTrackingEvents.USER_SUCCESSFULY_PAID, {
        trip_id: tripId,
        trip_name: trip.name,
        order_id: orderId,
      });
    },
    onPaymentFailure: ({ errorType, errorMessage }) => {
      rudderAnalytics.track(TripTrackingEvents.USER_UNSUCCESSFULY_PAID, {
        trip_id: tripId,
        trip_name: trip.name,
        error_type: errorType,
        error_message: errorMessage,
      });
    },
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        navigate({ to: "../success", search: { orderId } }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  useEffect(() => {
    rudderAnalytics.track(TripTrackingEvents.USER_REVIEWED_TRIP_SUMMARY, {
      trip_id: tripId,
      trip_name: trip.name,
      total_price: totalPrice,
    });
  }, [tripId, trip.name, totalPrice]);

  return (
    <>
      <h2 className="mb-4 text-xl font-medium text-text-title">
        {t("trips.summaryPage.title")}
      </h2>

      <div className="space-y-3">
        <OverviewCard trip={trip} />

        <TripDetailsCard trip={trip} />

        <TravellerListCard>
          {travellersArray.map(({ id, name, additionalData }) => (
            <TravellerListItem
              key={id}
              name={name}
              additionalData={additionalData}
            />
          ))}
        </TravellerListCard>

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          classNames={{
            wrapper: "-mx-4",
          }}
        />

        <PriceDetailsCard
          totalPrice={totalPrice}
          totalDiscount={totalDiscount}
          totalDiscountedPrice={totalDiscountedPrice}
        />

        <Controller<BookingData, "termsAndConditions">
          name="termsAndConditions"
          render={({ field: { onChange, onBlur, value } }) => (
            <TermsAndConditionsCard
              checked={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </div>

      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          disabled={termsAndConditionsInvalid}
          loading={isQiPaymentPending}
          onClick={() => {
            handleSubmit((data) =>
              mutateAsync({
                packageId: tripId,
                startDate: data.tripDate,
                travelersIds: Object.values(data.selectedTravellers)
                  .flat()
                  .filter((x): x is NonNullable<typeof x> => Boolean(x)),
                numberOfAdultsSingleRoom: data.travellers.adultInSingleRoom,
                numberOfAdultsDoubleRoom: data.travellers.adultInDoubleRoom,
                numberOfChildrenWithBed: data.travellers.childWithBed,
                numberOfChildrenWithoutBed: data.travellers.childWithoutBed,
                numberOfInfants: data.travellers.infant,
              }),
            )();
          }}
        >
          {t("trips.book")}
        </Button>
      </PersistentDrawer>
    </>
  );
}
